import { useEffect, useState } from "react";
import { masterApi, apiError, formatBRL, formatL, PRODUTOS_LABEL } from "@/lib/api";
import Loader, { useMinLoading } from "@/components/Loader";
import { useUnit } from "@/pages/Master/MasterApp";
import { modal } from "@/lib/modal";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, Users, Droplets, ArrowUpRight, ArrowDownRight, Car, Building2 } from "lucide-react";

export default function MasterDashboard() {
  const { unitId, unidades } = useUnit();
  const [data, setData] = useState(null);
  const [catData, setCatData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Inicio do dia em ISO (00:00:00) para o relatório de categorias filtrar HOJE
    const hojeInicio = new Date();
    hojeInicio.setHours(0, 0, 0, 0);
    const inicio = hojeInicio.toISOString();
    const unitQ = unitId !== "all" ? `&unit_id=${unitId}` : "";
    Promise.all([
      masterApi.get(`/master/dashboard?unit_id=${unitId}`),
      masterApi.get(`/master/relatorios/vendas-por-categoria?inicio=${inicio}${unitQ}`),
    ])
      .then(([d, c]) => { setData(d.data); setCatData(c.data); })
      .catch((e) => modal.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [unitId]);

  const showLoader = useMinLoading(loading || !data);
  if (showLoader) return <Loader />;

  const ticket = data.atendimentos_hoje ? data.faturamento_liquido_hoje / data.atendimentos_hoje : 0;
  const unitNome = unitId !== "all" ? (unidades?.find((u) => u.id === unitId)?.nome || "") : "";
  const varNovos = Number(data.variacao_novos_pct || 0);

  const normalShare = catData?.items?.find((i) => i.categoria === "normal")?.share_pct ?? 0;
  const appShare = catData?.items?.find((i) => i.categoria === "app_driver")?.share_pct ?? 0;
  const normalAtds = catData?.items?.find((i) => i.categoria === "normal")?.atendimentos ?? 0;
  const appAtds = catData?.items?.find((i) => i.categoria === "app_driver")?.atendimentos ?? 0;
  const hasCatData = (normalAtds + appAtds) > 0;

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-6 float-in" data-testid="master-dashboard">
      {unitNome && (
        <div className="inline-flex items-center gap-2 self-start px-3.5 py-2 rounded-lg" style={{ background: "rgba(10,42,115,0.08)" }} data-testid="dash-filial-ativa">
          <Building2 size={16} className="text-[color:var(--brand-blue)]" />
          <span className="text-[13px] font-bold uppercase tracking-wide text-[color:var(--brand-blue)]">Filial: {unitNome}</span>
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2">Visão geral · Hoje</div>
          <h1 className="font-serif-display text-3xl md:text-5xl leading-[1.05]">Como vão suas unidades?</h1>
        </div>
        {hasCatData && (
          <div data-testid="dash-categoria-mix"
               className="card-soft rounded-2xl px-4 py-3 min-w-[200px]"
               style={{ background: "linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%)" }}>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">Mix de clientes · hoje</div>
            <div className="flex items-center gap-3 mb-1.5">
              <span className="font-serif-display text-lg">{normalShare}%</span>
              <span className="text-[10px] text-neutral-500">Normal</span>
              <span className="text-neutral-300">/</span>
              <span className="font-serif-display text-lg text-amber-700">{appShare}%</span>
              <span className="text-[10px] text-amber-700 inline-flex items-center gap-1"><Car size={10} />App</span>
            </div>
            {/* Barra empilhada */}
            <div className="h-1.5 rounded-full overflow-hidden bg-neutral-100 flex">
              <div className="h-full bg-neutral-900" style={{ width: `${normalShare}%` }} />
              <div className="h-full bg-amber-600" style={{ width: `${appShare}%` }} />
            </div>
            <div className="mt-1.5 text-[10px] text-neutral-500">
              {normalAtds} normal · {appAtds} app driver
            </div>
          </div>
        )}
      </div>

      {/* Hero KPIs */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card-soft rounded-2xl p-6 relative overflow-hidden md:col-span-2"
             style={{ background: "linear-gradient(135deg,#EAF1FE 0%,#F6FAFF 100%)" }}>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(10,42,115,0.10)" }}>
              <TrendingUp size={15} className="text-[color:var(--brand-blue)]" />
            </span>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--brand-blue)]">Faturamento líquido · hoje</div>
          </div>
          <div className="font-serif-display text-4xl md:text-5xl mt-1 leading-none text-[color:var(--brand-blue)]">{formatBRL(data.faturamento_liquido_hoje)}</div>
          <div className="mt-3 flex items-center gap-3 text-xs text-neutral-500">
            <span><TrendingUp size={12} className="inline mr-1" /> {data.atendimentos_hoje} atendimentos</span>
            <span>· Ticket {formatBRL(ticket)}</span>
          </div>
        </div>
        <div className="card-soft rounded-2xl p-6 relative overflow-hidden"
             style={{ background: "linear-gradient(135deg,#FFF4E0 0%,#FFFBF3 100%)" }}>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(180,83,9,0.12)" }}>
              <Droplets size={15} className="text-amber-600" />
            </span>
            <div className="text-[10px] uppercase tracking-widest text-amber-700">Litros vendidos</div>
          </div>
          <div className="font-serif-display text-4xl md:text-5xl mt-1 leading-none text-amber-700">{formatL(data.litros_vendidos_hoje)}</div>
          <div className="mt-3 text-xs text-neutral-500">
            <Droplets size={12} className="inline mr-1" /> Descontos: {formatBRL(data.descontos_dados_hoje)}
          </div>
        </div>
      </section>

      {/* Mini KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SoftKpi label="Taxa identificação" value={`${data.taxa_identificacao_pct}%`} />
        <SoftKpi label="Novos cadastros · ontem"
                 value={String(data.novos_cadastros_ontem)}
                 trend={varNovos !== 0 ? `${varNovos >= 0 ? "+" : ""}${varNovos}%` : null}
                 trendUp={varNovos >= 0} />
        <SoftKpi label="Resgates Saudade" value={String(data.saudade_resgates_hoje)} icon={Users} highlight />
        <SoftKpi label="Ticket médio" value={formatBRL(ticket)} />
      </section>

      {/* Série 7 dias */}
      <section className="card-soft rounded-2xl p-5">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-serif-display text-xl">Últimos 7 dias</h3>
          <span className="text-[11px] text-neutral-500">Faturamento × Descontos</span>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data.serie_7d}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
            <XAxis dataKey="data" stroke="#737373" fontSize={11} />
            <YAxis stroke="#737373" fontSize={11} />
            <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e5e5e5", boxShadow: "0 8px 24px -10px rgba(0,0,0,.12)" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="faturamento" stroke="#1D4ED8" strokeWidth={2} dot={{ r: 3 }} name="Faturamento (R$)" />
            <Line type="monotone" dataKey="descontos" stroke="#E0975A" strokeWidth={2} dot={{ r: 3 }} name="Descontos (R$)" />
          </LineChart>
        </ResponsiveContainer>
      </section>

      {/* Unidades */}
      {data.unidades_faturamento?.length > 0 && (
        <section className="card-soft rounded-2xl p-5">
          <h3 className="font-serif-display text-xl mb-3">Comparativo entre unidades</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.unidades_faturamento}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis dataKey="nome" stroke="#737373" fontSize={11} />
              <YAxis stroke="#737373" fontSize={11} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e5e5e5" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="faturamento" fill="#1D4ED8" radius={[8, 8, 0, 0]} name="Faturamento" />
              <Bar dataKey="litros" fill="#0F172A" radius={[8, 8, 0, 0]} name="Litros" />
              <Bar dataKey="descontos" fill="#E0975A" radius={[8, 8, 0, 0]} name="Descontos" />
            </BarChart>
          </ResponsiveContainer>
        </section>
      )}

      {/* Preço médio por combustível */}
      <section>
        <h3 className="font-serif-display text-xl mb-3">Preço médio líquido / litro</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
          {Object.entries(data.preco_medio_liquido).map(([prod, val]) => (
            <div key={prod} className="card-soft rounded-xl p-3.5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">{PRODUTOS_LABEL[prod] || prod}</div>
              <div className="font-serif-display text-xl mt-0.5">{val > 0 ? formatBRL(val) : "—"}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function SoftKpi({ label, value, trend, trendUp, icon: Icon, highlight }) {
  return (
    <div className={`card-soft rounded-2xl p-4 ${highlight ? "bg-gradient-to-br from-orange-50 to-amber-50" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
        {Icon && <Icon size={13} className="text-neutral-400" />}
      </div>
      <div className="font-serif-display text-2xl mt-1">{value}</div>
      {trend && (
        <div className={`mt-1 inline-flex items-center gap-1 text-[11px] font-medium ${trendUp ? "text-emerald-700" : "text-rose-700"}`}>
          {trendUp ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />} {trend}
        </div>
      )}
    </div>
  );
}
